import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchPageBySlug } from '../lib/cms';
import Gallery from '../components/Gallery';

export default function SolfilmPage({ forcedType }) {
  const params = useParams();
  const type = forcedType || params.type || 'bil';
  const pageSlug = `solfilm-${type}`;

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Metadata og defaults per type
  const typeInfo = {
    bil: {
      defaultTitle: 'Solfilm til biler',
      badge: 'Personbiler, Stationcars & Varebiler',
      heroDefaultImg: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/stc-car-solfilm.jpg',
      icon: 'fa-solid fa-car-side'
    },
    maskin: {
      defaultTitle: 'Solfilm til maskiner',
      badge: 'Gravemaskiner, Traktorer & Entreprenør',
      heroDefaultImg: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/bobcat-20stor-20klar_1.webp',
      icon: 'fa-solid fa-tractor'
    },
    hus: {
      defaultTitle: 'Solfilm til hus & bygninger',
      badge: 'Bolig, Kontor & Erhvervsbygninger',
      heroDefaultImg: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/img-7987.jpeg',
      icon: 'fa-solid fa-house-chimney-window'
    }
  };

  const currentInfo = typeInfo[type] || typeInfo.bil;

  useEffect(() => {
    async function loadPage() {
      setLoading(true);
      const data = await fetchPageBySlug(pageSlug);
      setPage(data);
      setLoading(false);
    }
    loadPage();
  }, [pageSlug]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-red border-t-transparent" />
      </div>
    );
  }

  const heroImage =
    (page?.hero_billede && (Array.isArray(page.hero_billede) ? page.hero_billede[0] : page.hero_billede)) ||
    (page?.hero_image && (Array.isArray(page.hero_image) ? page.hero_image[0] : page.hero_image)) ||
    (page?.billeder && page.billeder[0]) ||
    (page?.images && page.images[0]) ||
    currentInfo.heroDefaultImg;
  const heroImageAlt = page?.hero_billede_alt || page?.hero_image_alt || page?.title || currentInfo.defaultTitle;

  const secondaryImage =
    (page?.mellem_billede && (Array.isArray(page.mellem_billede) ? page.mellem_billede[0] : page.mellem_billede)) ||
    (page?.sekundaert_billede && (Array.isArray(page.sekundaert_billede) ? page.sekundaert_billede[0] : page.sekundaert_billede)) ||
    (page?.secondary_image && (Array.isArray(page.secondary_image) ? page.secondary_image[0] : page.secondary_image)) ||
    (page?.billeder && page.billeder[1]) ||
    (page?.images && page.images[1]) ||
    heroImage;

  const secondaryImageAlt =
    page?.mellem_billede_alt ||
    page?.sekundaert_billede_alt ||
    page?.secondary_image_alt ||
    page?.title ||
    currentInfo.defaultTitle;

  // Toning niveauer med de specificerede ændringer:
  // "Mørkeste Toning" vs "Lys toning" på 35%
  const toningItems = [
    {
      percentage: '35%',
      title: 'Lys toning',
      desc: 'En 35% solfilm giver en diskret lys toning, der dæmper skarp sol og varme uden at gøre ruderne for mørke. Meget populær til sideruder og et elegant look.',
      opacityClass: 'bg-black/65',
      badge: 'Diskret lys toning & varmereduktion'
    },
    {
      percentage: '20%',
      title: 'Mellem Toning',
      desc: 'En 20% solfilm er den mellemste toning, der kommer tættest på den originale toning fra bilfabrikkerne. Velegnet til stationcars og familiebiler.',
      opacityClass: 'bg-black/80',
      badge: 'Tættest på original fabrikslook'
    },
    {
      percentage: '5%',
      title: 'Mørkeste Toning',
      desc: 'En 5% solfilm er den mørkeste solfilm, vi monterer. Den giver maksimalt privatliv og skygge for børn, hundeejere, varebiler eller maskiners bagruder.',
      opacityClass: 'bg-black/95',
      badge: 'Maksimalt privatliv & solstop'
    },
    {
      percentage: 'Måler',
      title: 'Udmåling med solfilmsmåler',
      desc: 'Vi har professionelt udmålingsudstyr, så vi præcist kan udmåle den eksisterende toning i dine ruder og matche den bedst muligt.',
      isSpecial: true,
      icon: 'fa-solid fa-gauge-high'
    }
  ];

  return (
    <div className="pb-24 space-y-20 lg:space-y-24 bg-slate-50/40">
      <Helmet>
        <title>{page?.meta_titel || page?.meta_title || `${currentInfo.defaultTitle} - CRE8IT`}</title>
        <meta name="description" content={page?.meta_beskrivelse || page?.meta_description || `${currentInfo.defaultTitle} hos CRE8IT. Effektiv varmereduktion, UV-beskyttelse og professionel montering.`} />
      </Helmet>

      {/* 1. Hero Sektion */}
      <section className="relative overflow-hidden bg-[#325d52] py-16 lg:py-24 border-b border-[#245b53]/40 text-white">
        {/* Subtile dekorative lysglød */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-96 w-96 rounded-full bg-[#deecd4]/15 blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            
            {/* Venstre kolonne: Tekst */}
            <div className="space-y-6 lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#deecd4] px-3.5 py-1.5 text-xs font-bold text-[#325d52] border border-[#deecd4] shadow-xs">
                <i className={currentInfo.icon} />
                <span>{currentInfo.badge}</span>
              </div>

              <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl leading-[1.15]">
                {page?.overskrift || page?.headline || (
                  <>
                    {currentInfo.defaultTitle.split(' ')[0]}{' '}
                    <span className="text-[#deecd4]">
                      {currentInfo.defaultTitle.split(' ').slice(1).join(' ')}
                    </span>
                  </>
                )}
              </h1>
              <div className="flex items-center gap-1.5 pt-0.5">
                <div className="h-1.5 w-12 rounded-full bg-[#deecd4]" />
                <div className="h-1.5 w-3 rounded-full bg-white/40" />
                <div className="h-1.5 w-1.5 rounded-full bg-[#deecd4]" />
              </div>

              <p className="text-lg text-emerald-50/90 leading-relaxed max-w-2xl">
                {page?.underoverskrift || page?.subtitle || 'Solfilm forhindrer det meste sollys i at trænge gennem ruderne, reducerer varmen og giver et langt mere behageligt indeklima.'}
              </p>
              
              <div className="flex flex-wrap gap-4 pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#deecd4] px-6 py-3.5 text-sm font-bold text-[#1b453f] shadow-md transition-all hover:bg-white hover:text-gray-900 cursor-pointer"
                >
                  <i className="fa-solid fa-envelope text-xs text-[#1b453f]" />
                  Få et uforpligtende tilbud
                </Link>
                <a
                  href="#galleri"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/60 bg-white/15 backdrop-blur-xs px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-white hover:text-gray-900 cursor-pointer group"
                >
                  <i className="fa-solid fa-images text-[#deecd4] group-hover:text-gray-900 transition-colors" />
                  Se eksempler
                </a>
              </div>
            </div>

            {/* Højre kolonne: Billede */}
            <div className="relative lg:col-span-5">
              <div className="aspect-[4/3] sm:aspect-square overflow-hidden rounded-3xl border border-gray-200 shadow-xl bg-white">
                <img
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/${heroImage.replace('https://assets.auxodev.dk/', '')}`}
                  alt={heroImageAlt}
                  className="h-full w-full object-cover"
                />
              </div>

              {/* Dekorativ badge */}
              <div className="absolute -bottom-5 -right-5 rounded-2xl bg-white p-4 shadow-xl border border-gray-100 hidden sm:flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-green-light/40 text-brand-red">
                  <i className="fa-solid fa-sun text-xl" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">UV-beskyttelse</p>
                  <p className="text-sm font-bold text-gray-900">Op til 99% UV-stop</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Undernavigation mellem Solfilmsiderne */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-center gap-3 p-2 bg-white rounded-2xl border border-gray-200/80 shadow-xs">
          <span className="text-xs font-bold uppercase tracking-wider text-gray-400 px-3 py-1 hidden sm:inline-block">
            Solfilmsområder:
          </span>
          <Link
            to="/solfilm/bil"
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              type === 'bil'
                ? 'bg-brand-red text-white shadow-xs'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <i className="fa-solid fa-car-side text-xs" /> Bil
          </Link>
          <Link
            to="/solfilm/maskin"
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              type === 'maskin'
                ? 'bg-brand-red text-white shadow-xs'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <i className="fa-solid fa-tractor text-xs" /> Maskin
          </Link>
          <Link
            to="/solfilm/hus"
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              type === 'hus'
                ? 'bg-brand-red text-white shadow-xs'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <i className="fa-solid fa-house-chimney-window text-xs" /> Hus
          </Link>
        </div>
      </section>

      {/* 2. Brødtekst & Præsentation */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          
          <div className="lg:col-span-5 order-last lg:order-first">
            <div className="aspect-[4/3] sm:aspect-[4/5] overflow-hidden rounded-3xl border border-gray-200 shadow-md bg-white">
              <img
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${secondaryImage.replace('https://assets.auxodev.dk/', '')}`}
                alt={secondaryImageAlt}
                className="h-full w-full object-cover"
              />
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-red">Behageligt klima & beskyttelse</span>
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Hvorfor vælge professionel <span className="text-brand-red">solfilm</span>?
            </h2>
            <div className="flex items-center gap-1.5 pt-0.5">
              <div className="h-1 w-10 rounded-full bg-brand-red" />
              <div className="h-1 w-2.5 rounded-full bg-brand-green-light" />
            </div>
            
            <div className="prose max-w-none">
              {page?.content ? (
                <div dangerouslySetInnerHTML={{ __html: page.content }} />
              ) : (
                <p>
                  Solfilm forhindrer det meste sollys i at trænge gennem ruderne. Det reducerer varmen i kabinen og giver et bedre indeklima for fører og passagerer.
                </p>
              )}
            </div>
          </div>

        </div>
      </section>

      {/* 3. Toning & Lysgennemgang (særligt på Bil og Maskin) */}
      {(type === 'bil' || type === 'maskin') && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-gray-200/90 bg-white p-8 sm:p-12 shadow-xs space-y-8">
            <div className="text-center max-w-2xl mx-auto space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-brand-red">Toning & Lysgennemgang</span>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Vælg den rette <span className="text-brand-red">toningsgrad</span>
              </h3>
              <div className="flex items-center justify-center gap-1.5 pt-0.5">
                <div className="h-1 w-2 rounded-full bg-brand-green-light" />
                <div className="h-1 w-12 rounded-full bg-brand-red" />
                <div className="h-1 w-2.5 rounded-full bg-brand-green-light" />
              </div>
              <p className="text-sm text-gray-600">
                Vi tilbyder solfilm i flere forskellige toningsgrader, tilpasset dit behov for udsyn og solbeskyttelse.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {toningItems.map((item, idx) => {
                if (item.isSpecial) {
                  return (
                    <div
                      key={idx}
                      className="rounded-2xl border border-gray-200 bg-brand-green-light/15 p-6 flex flex-col justify-between space-y-4 sm:col-span-2 lg:col-span-1"
                    >
                      <div className="space-y-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-red text-white shadow-xs">
                          <i className={`${item.icon} text-xl`} />
                        </div>
                        <h4 className="font-bold text-gray-900 text-base">{item.title}</h4>
                        <p className="text-xs text-gray-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={idx}
                    className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-xs flex flex-col justify-between hover:shadow-sm transition-all"
                  >
                    {/* Tone Preview boks */}
                    <div className="h-24 bg-gray-100 relative flex items-center justify-center overflow-hidden border-b border-gray-100">
                      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#245b53_1px,transparent_1px)] [background-size:12px_12px]" />
                      <div className={`absolute inset-0 ${item.opacityClass} flex items-center justify-center text-white font-extrabold text-2xl`}>
                        {item.percentage}
                      </div>
                    </div>

                    <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
                      <div>
                        <div className="flex items-center justify-between">
                          <h4 className="font-bold text-gray-900 text-sm">{item.title}</h4>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-green-light/40 text-brand-red">
                            {item.percentage}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-2 leading-relaxed">{item.desc}</p>
                      </div>
                      <div className="pt-2 border-t border-gray-100 text-[11px] font-medium text-brand-red flex items-center gap-1.5">
                        <i className="fa-solid fa-circle-check text-[10px]" />
                        <span>{item.badge}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 4. Priseksempel: KUN Stc. Car vises med bilbillede og UDEN "Bestil tid"-CTA */}
      {type === 'bil' && (
        <section id="priser" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-gray-200/90 bg-white p-8 sm:p-12 shadow-xs space-y-8">
            <div className="text-center max-w-2xl mx-auto space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-brand-red">Gennemskuelighed</span>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center justify-center gap-2.5">
                <i className="fa-solid fa-tags text-brand-red" />
                Priseksempel på <span className="text-brand-red">Solfilm</span>
              </h3>
              <div className="flex items-center justify-center gap-1.5 pt-0.5">
                <div className="h-1 w-2 rounded-full bg-brand-green-light" />
                <div className="h-1 w-12 rounded-full bg-brand-red" />
                <div className="h-1 w-2.5 rounded-full bg-brand-green-light" />
              </div>
              <p className="text-sm text-gray-600">
                Nedenfor ses et konkret priseksempel på montering af solfilm på en standard stationcar / personbil.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 rounded-3xl border border-gray-200 bg-gradient-to-b from-white to-slate-50/60 overflow-hidden shadow-md items-stretch">
                {/* Billede af Tesla / Stc. Car i værkstedsmiljø (VENSTRE) */}
                <div className="relative min-h-[260px] sm:min-h-[320px] md:min-h-full w-full overflow-hidden bg-gray-100">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/stc-car-solfilm.jpg"
                    alt="Priseksempel på Solfilm til Stc. Car / Stationcar"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute top-4 left-4 rounded-full bg-brand-red px-3 py-1 text-xs font-bold text-white shadow-xs">
                    Priseksempel
                  </div>

                  {/* Eksklusiv gul prisbobbel / badge på billedet */}
                  <div className="absolute top-4 right-4 transform hover:scale-105 transition-transform duration-200">
                    <div className="relative flex flex-col items-center justify-center rounded-2xl bg-gradient-to-br from-amber-300 via-yellow-400 to-amber-400 px-4 py-2.5 shadow-xl ring-2 ring-white/95 border border-yellow-200/60 rotate-2">
                      <span className="text-[9px] font-black uppercase tracking-wider text-amber-950/80 leading-none flex items-center gap-1">
                        <i className="fa-solid fa-sparkles text-[8px] text-amber-900" /> Fast lav pris
                      </span>
                      <div className="flex items-baseline gap-0.5 mt-0.5">
                        <span className="text-xl sm:text-2xl font-black text-gray-950 tracking-tight leading-none">2.800,-</span>
                      </div>
                      <span className="text-[9px] font-bold text-amber-950/90 leading-none mt-0.5">inkl. moms</span>
                    </div>
                  </div>
                </div>

                {/* Pris og beskrivelse (HØJRE) */}
                <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-between space-y-6">
                  <div className="space-y-4">
                    <div className="border-b border-gray-200/80 pb-4">
                      <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-red mb-1">
                        <i className="fa-solid fa-car-side" /> Personbil / Stc.
                      </div>
                      <h4 className="text-2xl sm:text-3xl font-extrabold text-gray-900">Stationcar (Stc.)</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Bagrude + bagagerumsruder + trekantruder + sideruder
                      </p>
                    </div>

                    <div className="space-y-3 text-xs sm:text-sm text-gray-700">
                      <div className="flex items-start gap-2.5">
                        <i className="fa-solid fa-circle-check text-emerald-600 mt-0.5 shrink-0" />
                        <span><strong>7 års fuld garanti</strong> på både folie og montering</span>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <i className="fa-solid fa-circle-check text-emerald-600 mt-0.5 shrink-0" />
                        <span><strong>Hurtig montering</strong> (typisk ca. 1,5 time)</span>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <i className="fa-solid fa-circle-check text-emerald-600 mt-0.5 shrink-0" />
                        <span>Udført på vores professionelle værksted i <strong>Rødding</strong></span>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <i className="fa-solid fa-circle-check text-emerald-600 mt-0.5 shrink-0" />
                        <span>Valgfri toningsgrad (<strong>35%, 20%, 5%</strong> lysgennemgang)</span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-100 text-xs text-gray-500">
                    <em>Kontakt os gerne for et skræddersyet tilbud på andre bilmodeller, varebiler eller specialkøretøjer.</em>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 5. Specifikationer */}
      {(page?.specifikationer || page?.specifications) && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-gray-200/90 bg-white p-8 sm:p-12 shadow-xs space-y-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-green-light/30 text-brand-red">
                <i className="fa-solid fa-list-check" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Specifikationer & Fordele</h3>
            </div>
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: page.specifikationer || page.specifications }} />
          </div>
        </section>
      )}

      {/* 6. Galleri Sektion */}
      <section id="galleri" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 border-t border-gray-200/80 pt-16 space-y-8">
        <div className="text-center max-w-2xl mx-auto space-y-2">
          <span className="text-xs font-bold uppercase tracking-wider text-brand-red">Inspiration & Referencer</span>
          <h2 className="text-3xl font-bold text-gray-900">Se vores seneste opgaver</h2>
          <p className="text-sm text-gray-600">
            Klik på et billede for at se det i stor visning.
          </p>
        </div>

        <Gallery slug={pageSlug} />
      </section>
    </div>
  );
}
